import * as React from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { registerUser } from '../api/authApi';
import backgroundImage from '../assets/bg-login.jpg';
import logo from '../assets/smiledent-logo.webp';
import getApiErrorMessage from '../utils/getApiErrorMessage';

export default function SignUpPage() {
  const [form, setForm] = React.useState({
    name: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarConfig, setSnackbarConfig] = React.useState({
    message: '',
    severity: 'success',
  });

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const password = form.password;

    if (!name || !email || !password) {
      setError('Please fill in your name, email, and password to sign up.');
      setSnackbarConfig({
        message: 'Please complete all fields before continuing.',
        severity: 'warning',
      });
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await registerUser({ name, email, password });

      // Show success Snackbar
      setSnackbarConfig({
        message: '✅ Registered! Check your inbox to verify your email.',
        severity: 'success',
      });
      setSnackbarOpen(true);

      // After a short delay, redirect to sign-in
      // setTimeout(() => {
      //   navigate('/signin');
      // }, 2000);
    } catch (e) {
      console.error('Registration failed:', e);
      const message = getApiErrorMessage(
        e,
        'Registration failed. Please try again.'
      );
      setError(message);

      // Show error Snackbar
      setSnackbarConfig({
        message,
        severity: 'error',
      });
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 4 },
            width: 420,
            maxWidth: '90%',
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            textAlign: 'center',
            borderTop: '4px solid #e6401f',
          }}
        >
          <img
            src={logo}
            alt="SmileDent Dental Studio"
            style={{ width: 290, maxWidth: '100%', marginBottom: 16 }}
          />
          <Typography variant="h6" gutterBottom>
            Create an Account
          </Typography>

          {error && (
            <Typography variant="body2" color="error" gutterBottom>
              {error}
            </Typography>
          )}

          <TextField
            label="Full Name"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={handleChange('name')}
          />

          <TextField
            label="Email Address"
            fullWidth
            margin="normal"
            value={form.email}
            onChange={handleChange('email')}
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            value={form.password}
            onChange={handleChange('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            py: 1.1,
            backgroundColor: '#e6401f',
            '&:hover': { backgroundColor: '#b72d12' },
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Registering…' : 'Register'}
        </Button>

          <Box mt={2}>
            <Link href="/signin" variant="body2" underline="hover">
              Already have an account? Sign in
            </Link>
          </Box>
        </Paper>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarConfig.severity}
          variant="filled"
        >
          {snackbarConfig.message}
        </Alert>
      </Snackbar>
    </>
  );
}
